import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import toast from 'react-hot-toast';
import { useAuth } from 'shared/hooks/useAuth';
import useProfilePhotoSrc from 'shared/hooks/useProfilePhotoSrc';
import DeploymentStatusTable from '../../../shared/DeploymentStatusTable';
import {
  emptyDeploymentStatusDates,
  emptyDeploymentStatusApprovals,
} from '../../../shared/DeploymentLifecycleFields';
import { DEPLOYMENT_STATUS, formatEnumLabel } from '../../../constants/enums';
import { softwareApi } from '../../../api/systemRegisterApi';
import { unwrapData } from '../../../utils/responseHelpers';
import {
  getReportFormPalette as getFormPalette,
  REPORT_PRINT_KEEP_CSS,
  reportHeaderPrintSx,
} from 'shared/utils/reportFormPalette';
import { cropCanvas, downloadBlobFile } from 'shared/utils/downloadFormPdf';

const REPORT_PRINT_ROOT_ID = 'software-registration-report';
const PRINTING_BODY_CLASS = 'sr-printing-view-report';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function getSafeFileBaseName(name) {
  return (
    (name || 'software-registration-report')
      .replace(/[^\w\-]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, 60) || 'software-registration-report'
  );
}

const HEADER_BLUE = '#0D2240';

const APPROVAL_STATUS_COLORS = {
  full: { bg: '#E8F5E9', color: '#1B5E20' },
  partial: { bg: '#FFF8E1', color: '#F57F17' },
  declined: { bg: '#FFEBEE', color: '#B71C1C' },
  none: { bg: '#ECEFF1', color: '#455A64' },
};

function dash(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

/** Group documents by deployment status (enum order). Only statuses with filed docs. */
function buildDocumentGroupsByDeploymentStatus(docs = []) {
  const byStatus = new Map();
  (docs ?? []).forEach((d) => {
    const name = (d.documentName || d.fileName || d.location || '').trim();
    if (!name) return;
    const status = d.deploymentStatus || '';
    if (!byStatus.has(status)) byStatus.set(status, []);
    byStatus.get(status).push(name);
  });

  const rows = [];
  DEPLOYMENT_STATUS.forEach((status) => {
    const names = byStatus.get(status);
    if (!names?.length) return;
    rows.push({
      key: status,
      deploymentStatus: status,
      documents: names.join(', '),
    });
    byStatus.delete(status);
  });
  byStatus.forEach((names, status) => {
    if (!names?.length) return;
    rows.push({
      key: status || 'UNASSIGNED',
      deploymentStatus: status || null,
      documents: names.join(', '),
    });
  });
  return rows;
}

function slotRole(slot) {
  return slot === 1 ? '1st Approval' : '2nd Approval';
}

function findSlotApproval(approvals, slot) {
  const role = slotRole(slot);
  return (approvals ?? []).find((a) => a?.approverRole === role) || null;
}

function getApprovalSummary(approvals) {
  if (typeof approvals === 'string' && approvals.trim()) {
    const label = approvals.trim();
    const key = label.toLowerCase().includes('fully')
      ? 'full'
      : label.toLowerCase().includes('declin')
        ? 'declined'
        : label.toLowerCase().includes('awaiting')
          ? 'partial'
          : 'none';
    return { key, label };
  }
  const first = findSlotApproval(approvals, 1);
  const second = findSlotApproval(approvals, 2);
  const firstApproved = first?.approvalStatus === 'APPROVED';
  const secondApproved = second?.approvalStatus === 'APPROVED';
  const anyDeclined = [first, second].some((a) => a?.approvalStatus === 'REJECTED');
  if (firstApproved && secondApproved) return { key: 'full', label: 'Fully approved' };
  if (anyDeclined) return { key: 'declined', label: 'Declined' };
  if (firstApproved || secondApproved) {
    return { key: 'partial', label: `Awaiting ${firstApproved ? '2nd' : '1st'} approval` };
  }
  return { key: 'none', label: 'Not approved' };
}

const FieldCell = ({ label, children, sx, colSpan, border, muted }) => (
  <TableCell
    colSpan={colSpan}
    sx={{
      border,
      p: 1.25,
      verticalAlign: 'top',
      color: 'inherit',
      ...sx,
    }}
  >
    <Typography
      component="span"
      sx={{ fontWeight: 700, fontSize: '0.78rem', display: 'block', mb: 0.5, color: 'inherit' }}
    >
      {label}
    </Typography>
    <Box sx={{ minHeight: 22, fontSize: '0.9rem', color: 'inherit', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {children ?? <Box sx={{ color: muted, opacity: 0.7 }}>—</Box>}
    </Box>
  </TableCell>
);

function SectionTitle({ form, children }) {
  return (
    <Box
      data-report-section-title
      px={1.5}
      py={1}
      sx={{
        borderTop: form.border,
        bgcolor: form.headBg,
        '@media print': {
          bgcolor: '#f5f5f5 !important',
          borderTop: '1px solid #1a1a1a !important',
        },
      }}
    >
      <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: 'inherit', letterSpacing: 0.3 }}>
        {children}
      </Typography>
    </Box>
  );
}

function FormDataTable({ form, isDark, columns, rows, emptyMessage }) {
  const approvalChipSx = (key) => {
    const palette = APPROVAL_STATUS_COLORS[key] || APPROVAL_STATUS_COLORS.none;
    return {
      bgcolor: isDark ? alpha(palette.color, 0.28) : palette.bg,
      color: isDark ? '#fff' : palette.color,
      fontWeight: 700,
    };
  };

  return (
    <Table size="small" sx={{ tableLayout: 'fixed', color: 'inherit' }}>
      <TableHead>
        <TableRow>
          {columns.map((col) => (
            <TableCell
              key={col.key}
              sx={{
                border: form.border,
                fontWeight: 800,
                fontSize: '0.78rem',
                bgcolor: form.headBg,
                color: 'inherit',
                py: 1,
                width: col.width,
                '@media print': {
                  bgcolor: '#f5f5f5 !important',
                  color: '#111 !important',
                  border: '1px solid #1a1a1a !important',
                },
              }}
            >
              {col.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell
              colSpan={columns.length}
              sx={{ border: form.border, color: form.muted, fontSize: '0.85rem', py: 1.5 }}
            >
              {emptyMessage}
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row) => (
            <TableRow key={row.id ?? row.key}>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  sx={{
                    border: form.border,
                    verticalAlign: 'top',
                    fontSize: '0.85rem',
                    color: 'inherit',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {col.render ? col.render(row, approvalChipSx) : dash(row[col.key])}
                </TableCell>
              ))}
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

function narrativeToItems(value) {
  const text = String(value || '').trim();
  if (!text) return [];
  const parts = text
    .split(';')
    .map((part) => part.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
  if (parts.length >= 3) {
    return parts.map((part) => (part.endsWith('.') ? part : `${part}.`));
  }
  return [text];
}

function NarrativeBlock({ value, muted }) {
  const items = narrativeToItems(value);
  if (!items.length) {
    return (
      <Box sx={{ color: muted, opacity: 0.7, fontSize: '0.86rem' }}>—</Box>
    );
  }
  if (items.length === 1) {
    return (
      <Box
        sx={{
          fontSize: '0.86rem',
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          color: 'inherit',
        }}
      >
        {items[0]}
      </Box>
    );
  }
  return (
    <Box
      component="ul"
      sx={{
        m: 0,
        pl: 2.25,
        fontSize: '0.86rem',
        lineHeight: 1.5,
        color: 'inherit',
      }}
    >
      {items.map((item) => (
        <Box component="li" key={item} sx={{ mb: 0.45, wordBreak: 'break-word' }}>
          {item}
        </Box>
      ))}
    </Box>
  );
}

function RiskRegisterReportList({ form, rows, emptyMessage }) {
  if (!rows?.length) {
    return (
      <Box
        sx={{
          borderTop: form.border,
          px: 1.5,
          py: 1.5,
          color: form.muted,
          fontSize: '0.85rem',
        }}
      >
        {emptyMessage}
      </Box>
    );
  }

  return (
    <Box>
      {rows.map((row, index) => (
        <Box
          key={row.id ?? row.key ?? index}
          className="risk-report-card"
          data-pdf-keep="true"
          sx={{
            borderTop: form.border,
            '@media print': {
              breakInside: 'avoid',
              pageBreakInside: 'avoid',
              borderTop: '1px solid #1a1a1a !important',
            },
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 0.85,
              bgcolor: form.headBg,
              display: 'flex',
              alignItems: 'baseline',
              gap: 1,
              '@media print': {
                bgcolor: '#f5f5f5 !important',
              },
            }}
          >
            <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', letterSpacing: 0.2, color: 'inherit' }}>
              Risk {index + 1}
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '0.86rem', color: 'inherit' }}>
              {dash(row.riskName)}
            </Typography>
          </Box>
          <Table size="small" sx={{ tableLayout: 'fixed', color: 'inherit' }}>
            <TableBody>
              <TableRow>
                <FieldCell label="Risk name:" border={form.border} muted={form.muted} sx={{ width: '34%' }}>
                  {dash(row.riskName)}
                </FieldCell>
                <FieldCell label="Category:" border={form.border} muted={form.muted} sx={{ width: '33%' }}>
                  {formatEnumLabel(row.riskCategory) || '—'}
                </FieldCell>
                <FieldCell label="Priority:" border={form.border} muted={form.muted} sx={{ width: '33%' }}>
                  {formatEnumLabel(row.riskPriority) || '—'}
                </FieldCell>
              </TableRow>
              <TableRow>
                <FieldCell label="Resources impacted:" border={form.border} muted={form.muted}>
                  {dash(row.resourcesImpacted)}
                </FieldCell>
                <FieldCell label="Owner:" border={form.border} muted={form.muted}>
                  {dash(row.riskOwner)}
                </FieldCell>
                <FieldCell label="Status:" border={form.border} muted={form.muted}>
                  {formatEnumLabel(row.status) || '—'}
                </FieldCell>
              </TableRow>
              <TableRow>
                <FieldCell label="Risk description:" border={form.border} muted={form.muted} colSpan={3}>
                  <NarrativeBlock value={row.riskDescription} muted={form.muted} />
                </FieldCell>
              </TableRow>
              <TableRow>
                <FieldCell label="Impact:" border={form.border} muted={form.muted} colSpan={3}>
                  <NarrativeBlock value={row.impact} muted={form.muted} />
                </FieldCell>
              </TableRow>
              <TableRow>
                <FieldCell label="Causes:" border={form.border} muted={form.muted} colSpan={3}>
                  <NarrativeBlock value={row.causes} muted={form.muted} />
                </FieldCell>
              </TableRow>
              <TableRow>
                <FieldCell label="Controls & mitigation:" border={form.border} muted={form.muted} colSpan={3}>
                  <NarrativeBlock value={row.controlsAndMitigation} muted={form.muted} />
                </FieldCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      ))}
    </Box>
  );
}

/**
 * View Report — paper form template (same design as Monitoring & Review Form).
 */
export default function WizardStepViewReport({
  wizardData,
  lookups = {},
  softwareId = null,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const profile = user?.data;
  const tenantBranding = profile?.tenantBranding;
  const form = useMemo(
    () => getFormPalette(isDark, tenantBranding),
    [
      isDark,
      tenantBranding?.backgroundColor,
      tenantBranding?.primaryColor,
      tenantBranding?.secondaryColor,
    ]
  );
  const tenantLogoSrc = useProfilePhotoSrc(tenantBranding?.tenantPhoto);
  const tenantDisplayName =
    (profile?.tenantName && String(profile.tenantName).trim())
    || (tenantBranding?.name && String(tenantBranding.name).trim())
    || 'Organisation';

  useEffect(() => {
    if (!softwareId) {
      setReport(null);
      return undefined;
    }
    let cancelled = false;
    softwareApi
      .report(softwareId)
      .then((res) => {
        if (!cancelled) setReport(unwrapData(res));
      })
      .catch(() => {
        if (!cancelled) setReport(null);
      });
    return () => {
      cancelled = true;
    };
  }, [softwareId]);

  // Print CSS only applies while body has the printing class (set by Print button).
  useEffect(() => {
    const style = document.createElement('style');
    style.setAttribute('data-sr-view-report-print', 'true');
    style.textContent = `
      /* Zero page margin hides Chrome/Edge date, title, and URL header/footer. */
      @page {
        size: A4;
        margin: 0;
      }
      @media print {
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          background: #fff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        body.${PRINTING_BODY_CLASS} .no-print { display: none !important; }
        body.${PRINTING_BODY_CLASS} * { visibility: hidden !important; }
        body.${PRINTING_BODY_CLASS} #${REPORT_PRINT_ROOT_ID},
        body.${PRINTING_BODY_CLASS} #${REPORT_PRINT_ROOT_ID} * {
          visibility: visible !important;
        }
        body.${PRINTING_BODY_CLASS} #${REPORT_PRINT_ROOT_ID} {
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
          margin: 0 !important;
          padding: 12mm !important;
          box-sizing: border-box !important;
          box-shadow: none !important;
          overflow: visible !important;
          border: none !important;
        }
        body.${PRINTING_BODY_CLASS} #${REPORT_PRINT_ROOT_ID} .risk-report-card {
          break-inside: avoid;
          page-break-inside: avoid;
        }
        ${REPORT_PRINT_KEEP_CSS}
      }
    `;
    document.head.appendChild(style);
    return () => {
      style.remove();
      document.body.classList.remove(PRINTING_BODY_CLASS);
    };
  }, []);

  const id = wizardData?.softwareIdentification ?? {};
  const useCase = wizardData?.useCase ?? {};
  const risk = wizardData?.riskImpact ?? {};
  const compliance = wizardData?.complianceGovernance ?? {};
  const dm = wizardData?.dataModel ?? {};

  const resolveNames = (ids, options, labelKey) =>
    (ids ?? [])
      .map((itemId) => options?.find((o) => o.id === itemId)?.[labelKey])
      .filter(Boolean)
      .join(', ') || '—';

  const softwareName = report?.softwareName || id.softwareName || '—';
  const businessUnits =
    report?.businessUnits || resolveNames(id.businessUnitIds, lookups.businessUnits, 'name');
  const systemOwners =
    report?.systemOwners || resolveNames(id.systemOwnerIds, lookups.systemOwners, 'fullName');
  const vendors =
    report?.vendors || resolveNames(id.vendorIds, lookups.vendors, 'vendorName');
  const regulations =
    report?.regulations
    || resolveNames(
      (risk.regulationIds?.length ? risk.regulationIds : compliance.regulationIds),
      lookups.regulations,
      'regulationName'
    );

  const durationLabel = (amount, unit) => {
    if (!amount && !unit) return '—';
    return [amount, formatEnumLabel(unit)].filter(Boolean).join(' ');
  };

  const contractDurationLabel = durationLabel(
    report?.contractDuration ?? id.contractDuration,
    report?.contractDurationUnit ?? id.contractDurationUnit
  );
  const licenseDurationLabel = durationLabel(
    report?.licenseDuration ?? id.licenseDuration,
    report?.licenseDurationUnit ?? id.licenseDurationUnit
  );

  const businessProcessRows = useMemo(() => {
    if (Array.isArray(report?.businessProcesses) && report.businessProcesses.length) {
      return report.businessProcesses.map((row) => ({
        id: row.id ?? row.businessProcessId,
        businessProcessName: row.businessProcessName,
        businessOwner: row.businessOwner,
        decisionType: row.decisionType,
        humanOversight: row.humanOversight,
        oversightExplanation: row.oversightExplanation,
      }));
    }
    return (useCase.businessProcesses ?? []).map((row) => ({
      id: row.id ?? row.key ?? row.businessProcessId,
      businessProcessName:
        lookups.businessProcesses?.find((p) => p.id === row.businessProcessId)?.name
        || row.businessProcessName
        || '—',
      businessOwner: row.businessOwner,
      decisionType: row.decisionType,
      humanOversight: row.humanOversight,
      oversightExplanation: row.humanOversightExplanation || row.oversightExplanation,
    }));
  }, [report?.businessProcesses, useCase.businessProcesses, lookups.businessProcesses]);

  const monitoringRows = useMemo(() => {
    if (report?.monitoringReviews?.length) {
      return report.monitoringReviews.map((r) => ({
        id: r.id,
        reviewAreaControl: r.reviewAreaControl,
        riskRating: r.riskRating,
        status: r.status,
        nextReviewDate: r.nextReviewDate,
        approvalStatus: r.approvalStatus,
      }));
    }
    return (wizardData?.monitoringReview?.entries ?? []).map((r) => ({
      id: r.id ?? r.key,
      key: r.key,
      reviewAreaControl: r.reviewAreaControl,
      riskRating: r.riskRating,
      status: r.status,
      nextReviewDate: r.nextReviewDate,
      approvals: r.approvals,
    }));
  }, [report, wizardData?.monitoringReview?.entries]);

  const incidentRows = useMemo(() => {
    if (report?.incidents?.length) {
      return report.incidents.map((r) => ({
        id: r.id,
        incidentTicket: r.incidentTicket,
        incidentDetails: r.incidentDetails,
        severityLevel: r.severityLevel,
        resolutionStatus: r.resolutionStatus,
        incidentResponseOwner: r.incidentResponseOwner,
        approvalStatus: r.approvalStatus,
      }));
    }
    return (wizardData?.incidentEscalation?.entries ?? []).map((r) => ({
      id: r.id ?? r.key,
      key: r.key,
      incidentTicket: r.incidentTicket,
      incidentDetails: r.incidentDetails,
      severityLevel: r.severityLevel,
      resolutionStatus: r.resolutionStatus,
      incidentResponseOwner: r.incidentResponseOwner,
      approvals: r.approvals,
    }));
  }, [report, wizardData?.incidentEscalation?.entries]);

  const softwareRiskRows = useMemo(() => {
    if (report?.softwareRisks?.length) {
      return report.softwareRisks.map((r) => ({
        id: r.id,
        riskName: r.riskName,
        riskCategory: r.riskCategory,
        resourcesImpacted: r.resourcesImpacted,
        riskPriority: r.riskPriority,
        riskOwner: r.riskOwner,
        status: r.status,
        riskDescription: r.riskDescription,
        impact: r.impact,
        causes: r.causes,
        controlsAndMitigation: r.controlsAndMitigation,
      }));
    }
    return (risk.risks ?? []).map((r) => ({
      id: r.id ?? r.key,
      key: r.key,
      riskName: r.riskName,
      riskCategory: r.riskCategory,
      resourcesImpacted: r.resourcesImpacted,
      riskPriority: r.riskPriority,
      riskOwner: r.riskOwner,
      status: r.status,
      riskDescription: r.riskDescription,
      impact: r.impact,
      causes: r.causes,
      controlsAndMitigation: r.controlsAndMitigation,
    }));
  }, [report?.softwareRisks, risk.risks]);

  const changeRows = useMemo(() => {
    if (report?.changeManagements?.length) {
      return report.changeManagements.map((r) => ({
        id: r.id,
        title: r.title,
        changeType: r.changeType,
        priority: r.priority,
        implementationStatus: r.implementationStatus,
        requestedBy: r.requestedBy,
        approvalStatus: r.approvalStatus,
      }));
    }
    return (wizardData?.changeManagement?.entries ?? []).map((r) => ({
      id: r.id ?? r.key,
      key: r.key,
      title: r.title,
      changeType: r.changeType,
      priority: r.priority,
      implementationStatus: r.implementationStatus,
      requestedBy: r.requestedBy,
      approvals: r.approvals,
    }));
  }, [report, wizardData?.changeManagement?.entries]);

  const integrationRows = useMemo(() => {
    if (report?.integrations?.length) {
      return report.integrations.map((r) => ({
        id: r.id,
        sourceSystem: r.sourceSystem,
        targetSystem: r.targetSystem,
        integrationType: r.integrationType,
        dataFlowDirection: r.dataFlowDirection,
        integrationMethod: r.integrationMethod,
        integrationStatus: r.integrationStatus,
        integrationPurpose: r.integrationPurpose,
      }));
    }
    return (wizardData?.integration?.entries ?? []).map((r) => ({
      id: r.id ?? r.key,
      key: r.key,
      sourceSystem: r.sourceSystem,
      targetSystem: r.targetSystem,
      integrationType: r.integrationType,
      dataFlowDirection: r.dataFlowDirection,
      integrationMethod: r.integrationMethod,
      integrationStatus: r.integrationStatus,
      integrationPurpose: r.integrationPurpose,
    }));
  }, [report, wizardData?.integration?.entries]);

  const isIntegrated =
    report?.integratedWithOtherSystems != null
      ? Boolean(report.integratedWithOtherSystems)
      : Boolean(wizardData?.integration?.integratedWithOtherSystems);

  const documentGroupRows = useMemo(() => {
    if (report?.documentEntries?.length) {
      return buildDocumentGroupsByDeploymentStatus(report.documentEntries);
    }
    return buildDocumentGroupsByDeploymentStatus(compliance.documents ?? []);
  }, [report?.documentEntries, compliance.documents]);

  const usesAi =
    report?.usesArtificialIntelligence != null
      ? Boolean(report.usesArtificialIntelligence)
      : Boolean(risk.usesArtificialIntelligence);
  const dataSources =
    report?.dataSourcesUsed || (dm.dataSourcesUsed ?? '').trim() || '';
  const personalData =
    report?.personalData != null ? report.personalData : dm.personalData;
  const sensitive =
    report?.sensitiveDataCategories
    || (dm.sensitiveDataCategories ?? []).map((c) => formatEnumLabel(c)).filter(Boolean).join(', ');
  const modelType = formatEnumLabel(report?.modelType || dm.modelType);
  const trainingOrigin = formatEnumLabel(report?.trainingDataOrigin || dm.trainingDataOrigin);
  const hasAiDetails = Boolean(
    dataSources || personalData || sensitive || modelType || trainingOrigin
  );

  const renderApproval = (row, approvalChipSx) => {
    const summary = getApprovalSummary(row.approvalStatus ?? row.approvals);
    return <Chip size="small" label={summary.label} sx={approvalChipSx(summary.key)} />;
  };

  const namedSoftware =
    (report?.softwareName || id.softwareName || '').trim() || '';
  const reportTitle = namedSoftware
    ? `${namedSoftware} — Software Registration Report`
    : 'Software Registration Report';

  const buildReportPdf = useCallback(async () => {
    const formEl = document.getElementById(REPORT_PRINT_ROOT_ID);
    if (!formEl) {
      throw new Error('Report form not found.');
    }

    const host = document.createElement('div');
    host.setAttribute('aria-hidden', 'true');
    host.style.cssText =
      'position:fixed;left:-10000px;top:0;width:980px;background:#fff;z-index:-1;pointer-events:none;';
    const clone = formEl.cloneNode(true);
    clone.id = `${REPORT_PRINT_ROOT_ID}-pdf-clone`;
    clone.style.width = '980px';
    clone.style.maxWidth = '980px';
    clone.style.overflow = 'visible';
    clone.style.background = '#ffffff';
    clone.style.color = '#111111';
    clone.style.boxShadow = 'none';
    host.appendChild(clone);
    document.body.appendChild(host);

    const captureOptions = {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#ffffff',
      logging: false,
      imageTimeout: 15000,
    };

    try {
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

      const canvas = await html2canvas(clone, {
        ...captureOptions,
        width: clone.scrollWidth,
        height: clone.scrollHeight,
        windowWidth: clone.scrollWidth,
        windowHeight: clone.scrollHeight,
      });

      const headerEl = clone.querySelector('[data-report-header]');
      const headerCanvas = headerEl
        ? await html2canvas(headerEl, {
            ...captureOptions,
            width: headerEl.scrollWidth,
            height: headerEl.scrollHeight,
            windowWidth: headerEl.scrollWidth,
            windowHeight: headerEl.scrollHeight,
          })
        : null;

      const cloneRect = clone.getBoundingClientRect();
      const pxPerCanvasY = canvas.height / Math.max(cloneRect.height, 1);
      const sectionMetas = [];
      const sectionEls = Array.from(clone.querySelectorAll('[data-report-section-title]'));
      for (const el of sectionEls) {
        const rect = el.getBoundingClientRect();
        const topPx = (rect.top - cloneRect.top) * pxPerCanvasY;
        const titleCanvas = await html2canvas(el, {
          ...captureOptions,
          width: el.scrollWidth,
          height: el.scrollHeight,
          windowWidth: el.scrollWidth,
          windowHeight: el.scrollHeight,
        });
        sectionMetas.push({ topPx, titleCanvas });
      }

      const findActiveSection = (sourceYPx) => {
        let active = null;
        for (const section of sectionMetas) {
          if (section.topPx <= sourceYPx + 2) active = section;
          else break;
        }
        // Only repeat when the section started on a previous page.
        if (active && active.topPx < sourceYPx - 4) return active;
        return null;
      };

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true,
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const footerHeight = 12;
      const contentWidth = pageWidth - margin * 2;
      const mmPerPx = contentWidth / canvas.width;

      const headerHeightMm = headerCanvas ? headerCanvas.height * mmPerPx : 0;
      const continuedLabelMm = 5;

      let sourceYPx = 0;
      let pageIndex = 0;

      while (sourceYPx < canvas.height - 2) {
        if (pageIndex > 0) pdf.addPage();
        pageIndex += 1;

        const isContinuation = pageIndex > 1;
        const activeSection = isContinuation ? findActiveSection(sourceYPx) : null;

        let topOffsetMm = margin;
        if (isContinuation && headerCanvas) {
          pdf.addImage(
            headerCanvas.toDataURL('image/jpeg', 0.92),
            'JPEG',
            margin,
            topOffsetMm,
            contentWidth,
            headerHeightMm
          );
          topOffsetMm += headerHeightMm;

          pdf.setFontSize(8);
          pdf.setTextColor(90, 90, 90);
          pdf.text('Continued from previous page', pageWidth / 2, topOffsetMm + 3.2, {
            align: 'center',
          });
          topOffsetMm += continuedLabelMm;

          if (activeSection) {
            const sectionWidth = contentWidth;
            const sectionHeight =
              activeSection.titleCanvas.height * (sectionWidth / activeSection.titleCanvas.width);
            pdf.addImage(
              activeSection.titleCanvas.toDataURL('image/jpeg', 0.92),
              'JPEG',
              margin,
              topOffsetMm,
              sectionWidth,
              sectionHeight
            );
            topOffsetMm += sectionHeight;
          }
        }

        const availableMm = pageHeight - footerHeight - topOffsetMm;
        const availablePx = Math.max(1, availableMm / mmPerPx);
        const slice = cropCanvas(canvas, sourceYPx, Math.max(1, availablePx));
        const sliceHeightMm = slice.height * mmPerPx;

        pdf.addImage(
          slice.toDataURL('image/jpeg', 0.92),
          'JPEG',
          margin,
          topOffsetMm,
          contentWidth,
          sliceHeightMm
        );

        sourceYPx += availablePx;
      }

      const totalPages = pdf.internal.getNumberOfPages();
      for (let page = 1; page <= totalPages; page += 1) {
        pdf.setPage(page);
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');
        pdf.setDrawColor(180);
        pdf.setLineWidth(0.2);
        pdf.line(
          margin,
          pageHeight - footerHeight + 1.5,
          pageWidth - margin,
          pageHeight - footerHeight + 1.5
        );
        pdf.setFontSize(9);
        pdf.setTextColor(80, 80, 80);
        const label = namedSoftware
          ? `${namedSoftware}  ·  Page ${page} of ${totalPages}`
          : `Page ${page} of ${totalPages}`;
        pdf.text(label, pageWidth / 2, pageHeight - 5, { align: 'center' });
      }

      return pdf;
    } finally {
      if (host.parentNode) host.parentNode.removeChild(host);
    }
  }, [namedSoftware]);

  const handlePrint = useCallback(async () => {
    try {
      toast.loading('Preparing print…', { id: 'sr-report-print' });
      const pdf = await buildReportPdf();
      toast.dismiss('sr-report-print');

      const blob = pdf.output('blob');
      const url = URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.setAttribute('aria-hidden', 'true');
      iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
      iframe.src = url;
      document.body.appendChild(iframe);

      const cleanup = () => {
        window.removeEventListener('focus', cleanup);
        setTimeout(() => {
          iframe.remove();
          URL.revokeObjectURL(url);
        }, 1000);
      };

      iframe.onload = () => {
        const frameWindow = iframe.contentWindow;
        if (!frameWindow) {
          cleanup();
          return;
        }
        frameWindow.focus();
        frameWindow.print();
        window.addEventListener('focus', cleanup);
        setTimeout(cleanup, 60_000);
      };
    } catch (error) {
      console.error(error);
      toast.dismiss('sr-report-print');
      // Fallback to browser print of the on-screen form.
      const previousTitle = document.title;
      document.title = ' ';
      const cleanup = () => {
        document.title = previousTitle;
        document.body.classList.remove(PRINTING_BODY_CLASS);
        window.removeEventListener('afterprint', cleanup);
      };
      document.body.classList.add(PRINTING_BODY_CLASS);
      window.addEventListener('afterprint', cleanup);
      window.print();
      setTimeout(cleanup, 1500);
    }
  }, [buildReportPdf]);

  const handleDownloadPdf = useCallback(
    async (event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      if (downloadingPdf) return;

      setDownloadingPdf(true);
      const fileBase = getSafeFileBaseName(namedSoftware);
      const fileName = `${fileBase}-SOFTWARE-REGISTRATION-REPORT.pdf`;

      try {
        const pdf = await buildReportPdf();
        downloadBlobFile(pdf.output('blob'), fileName);
        toast.success('PDF downloaded.');
      } catch (error) {
        console.error(error);
        try {
          const formEl = document.getElementById(REPORT_PRINT_ROOT_ID);
          const html = `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>${escapeHtml(
            namedSoftware || 'Software Registration Report'
          )}</title></head><body>${formEl?.outerHTML || ''}</body></html>`;
          const blob = new Blob(['\ufeff', html], {
            type: 'application/msword;charset=utf-8',
          });
          downloadBlobFile(blob, `${fileBase}-SOFTWARE-REGISTRATION-REPORT.doc`);
          toast.success('Document downloaded.');
        } catch (fallbackError) {
          console.error(fallbackError);
          toast.error('Failed to download. Please try again.');
        }
      } finally {
        setDownloadingPdf(false);
      }
    },
    [buildReportPdf, downloadingPdf, namedSoftware]
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      pt={0.5}
      sx={{
        width: '100%',
        '@media print': {
          pt: 0,
          '& .no-print': { display: 'none !important' },
        },
      }}
    >
      <Box
        className="no-print"
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        mb={1.5}
        sx={{ width: '100%', maxWidth: 980 }}
      >
        <Button
          type="button"
          startIcon={<PrintOutlinedIcon />}
          onClick={handlePrint}
          variant="outlined"
          size="small"
        >
          Print
        </Button>
        <Button
          type="button"
          startIcon={
            downloadingPdf ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <PictureAsPdfOutlinedIcon />
            )
          }
          onClick={handleDownloadPdf}
          variant="contained"
          color="secondary"
          size="small"
          disabled={downloadingPdf}
        >
          {downloadingPdf ? 'Preparing PDF…' : 'Download PDF'}
        </Button>
      </Box>

      <Paper
        id={REPORT_PRINT_ROOT_ID}
        elevation={0}
        sx={{
          overflow: 'auto',
          bgcolor: form.surface,
          color: form.text,
          borderRadius: 0,
          border: form.border,
          maxWidth: 980,
          width: '100%',
          boxShadow: isDark
            ? `0 0 0 1px ${alpha('#fff', 0.04)}, 0 8px 28px ${alpha('#000', 0.45)}`
            : 'none',
          '@media print': {
            border: 'none',
            maxWidth: '100%',
            overflow: 'visible',
            bgcolor: '#fff !important',
            color: '#111 !important',
            boxShadow: 'none',
          },
        }}
      >
        {/* Banner — same pattern as Monitoring & Review Form */}
        <Box data-report-header>
          <Box
            sx={{
              bgcolor: form.banner,
              color: form.bannerText,
              px: { xs: 1.5, sm: 2.5 },
              py: 1.75,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
              '@media print': reportHeaderPrintSx,
            }}
          >
            <Box
              sx={{
                width: { xs: 108, sm: 148 },
                height: { xs: 72, sm: 96 },
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: form.logoBg,
                overflow: 'hidden',
                p: 0,
                border: isDark ? `1px solid ${alpha('#fff', 0.18)}` : `1px solid ${alpha('#000', 0.08)}`,
              }}
            >
              {tenantLogoSrc ? (
                <Box
                  component="img"
                  src={tenantLogoSrc}
                  alt={`${tenantDisplayName} logo`}
                  sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                />
              ) : (
                <Typography sx={{ color: HEADER_BLUE, fontWeight: 800, fontSize: '0.7rem', textAlign: 'center' }}>
                  LOGO
                </Typography>
              )}
            </Box>

            <Box flex={1} minWidth={140}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.15rem', sm: '1.5rem' },
                  letterSpacing: 0.2,
                  lineHeight: 1.25,
                  color: form.bannerText,
                }}
              >
                {tenantDisplayName}
              </Typography>
            </Box>

            <Box textAlign={{ xs: 'left', sm: 'right' }} flexShrink={0} maxWidth={{ xs: '100%', sm: 420 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '0.95rem', sm: '1.15rem' },
                  letterSpacing: 0.4,
                  lineHeight: 1.25,
                  color: form.bannerText,
                }}
              >
                {reportTitle}
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', mt: 0.35, opacity: 0.92, color: form.bannerText }}>
                SR VR1
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              height: 4,
              bgcolor: form.accent,
              '@media print': { bgcolor: `${form.accent} !important` },
            }}
          />
        </Box>
        <SectionTitle form={form}>1. Software identification</SectionTitle>
        <Table size="small" sx={{ tableLayout: 'fixed', color: 'inherit' }}>
          <TableBody>
            <TableRow>
              <FieldCell label="Software / System Name:" border={form.border} muted={form.muted} colSpan={2}>
                {dash(softwareName)}
              </FieldCell>
              <FieldCell label="Internal ID:" border={form.border} muted={form.muted}>
                {dash(report?.internalReference || id.internalReference)}
              </FieldCell>
            </TableRow>
            <TableRow>
              <FieldCell label="Organisation reference:" border={form.border} muted={form.muted} colSpan={3}>
                {dash(report?.externalOrganisationReference || id.externalOrganisationReference)}
              </FieldCell>
            </TableRow>
            <TableRow>
              <FieldCell label="Business units:" border={form.border} muted={form.muted}>
                {dash(businessUnits)}
              </FieldCell>
              <FieldCell label="System owners:" border={form.border} muted={form.muted}>
                {dash(systemOwners)}
              </FieldCell>
              <FieldCell label="Vendors:" border={form.border} muted={form.muted}>
                {dash(vendors)}
              </FieldCell>
            </TableRow>
          </TableBody>
        </Table>


        <SectionTitle form={form}>2. Contract</SectionTitle>
        <Table size="small" sx={{ tableLayout: 'fixed', color: 'inherit' }}>
          <TableBody>
            <TableRow>
              <FieldCell label="Start date:" border={form.border} muted={form.muted}>
                {dash(report?.contractStartDate || id.contractStartDate)}
              </FieldCell>
              <FieldCell label="End date:" border={form.border} muted={form.muted}>
                {dash(report?.contractEndDate || id.contractEndDate)}
              </FieldCell>
              <FieldCell label="Duration:" border={form.border} muted={form.muted}>
                {dash(contractDurationLabel)}
              </FieldCell>
            </TableRow>
          </TableBody>
        </Table>

        <SectionTitle form={form}>2b. License</SectionTitle>
        <Table size="small" sx={{ tableLayout: 'fixed', color: 'inherit' }}>
          <TableBody>
            <TableRow>
              <FieldCell label="Start date:" border={form.border} muted={form.muted}>
                {dash(report?.licenseStartDate || id.licenseStartDate)}
              </FieldCell>
              <FieldCell label="End date:" border={form.border} muted={form.muted}>
                {dash(report?.licenseEndDate || id.licenseEndDate)}
              </FieldCell>
              <FieldCell label="Duration:" border={form.border} muted={form.muted}>
                {dash(licenseDurationLabel)}
              </FieldCell>
            </TableRow>
          </TableBody>
        </Table>


        <SectionTitle form={form}>3. Deployment status</SectionTitle>
        <Table size="small" sx={{ tableLayout: 'fixed', color: 'inherit', mb: 1 }}>
          <TableBody>
            <TableRow>
              <FieldCell label="Deployment environment:" border={form.border} muted={form.muted} colSpan={3}>
                {dash(formatEnumLabel(report?.deploymentEnvironment || id.deploymentEnvironment))}
              </FieldCell>
            </TableRow>
            <TableRow>
              <FieldCell label="System IP address:" border={form.border} muted={form.muted} colSpan={3}>
                {dash(report?.systemIpAddress || id.systemIpAddress)}
              </FieldCell>
            </TableRow>
            <TableRow>
              <FieldCell label="System URL / path:" border={form.border} muted={form.muted} colSpan={3}>
                {dash(report?.systemUrl || id.systemUrl)}
              </FieldCell>
            </TableRow>
            <TableRow>
              <FieldCell label="Server name hosting the application:" border={form.border} muted={form.muted} colSpan={3}>
                {dash(report?.hostingServerName || id.hostingServerName)}
              </FieldCell>
            </TableRow>
          </TableBody>
        </Table>
        <Box sx={{ borderBottom: form.border, '& .MuiPaper-root': { border: 'none', borderRadius: 0, boxShadow: 'none' } }}>
          <DeploymentStatusTable
            deploymentStatus={report?.deploymentStatus || id.deploymentStatus || ''}
            deploymentStatusDates={
              report?.deploymentStatusDates
              || id.deploymentStatusDates
              || emptyDeploymentStatusDates()
            }
            deploymentStatusApprovals={
              id.deploymentStatusApprovals || emptyDeploymentStatusApprovals()
            }
            deploymentStatusesFromApi={
              report?.deploymentStatuses?.length
                ? report.deploymentStatuses
                : id.deploymentStatusesList || []
            }
            showAllStages
          />
        </Box>


        <SectionTitle form={form}>4. Business case</SectionTitle>
        {(() => {
          const bc = report?.businessCase || useCase.businessCase;
          if (!bc) {
            return (
              <Typography variant="body2" sx={{ color: form.muted, mb: 1.5 }}>
                No business case registered.
              </Typography>
            );
          }
          const rows = [
            ['Date', bc.caseDate],
            ['Submitted by', bc.submittedBy],
            ['Title / Role', bc.titleRole],
            ['1. Project overview', bc.projectOverview],
            ['2. Background / current situation', bc.backgroundCurrentSituation],
            ['3. Proposed solution', bc.proposedSolution],
            ['4. Technical requirements and integration', bc.technicalRequirementsIntegration],
            ['5. Security and compliance considerations', bc.securityComplianceConsiderations],
            ['6. Implementation plan', bc.implementationPlan],
            ['7. Cost estimate (CapEx / OpEx)', bc.costEstimate],
            ['8. Benefits / impacts on users', bc.benefitsImpactsOnUsers],
            ['9. Performance metrics', bc.performanceMetrics],
            ['10. Risks and limitations', bc.risksAndLimitations],
          ];
          return (
            <Table size="small" sx={{ tableLayout: 'fixed', color: 'inherit', mb: 1.5 }}>
              <TableBody>
                {rows.map(([label, value]) => (
                  <TableRow key={label}>
                    <FieldCell label={`${label}:`} border={form.border} muted={form.muted} colSpan={3}>
                      {dash(value)}
                    </FieldCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          );
        })()}


        <SectionTitle form={form}>5. Business processes</SectionTitle>
        <FormDataTable
          form={form}
          isDark={isDark}
          emptyMessage="No business processes registered."
          columns={[
            { key: 'businessProcessName', label: 'Business process', width: '22%' },
            { key: 'businessOwner', label: 'Business owner', width: '20%' },
            {
              key: 'decisionType',
              label: 'Decision type',
              width: '16%',
              render: (row) => formatEnumLabel(row.decisionType) || '—',
            },
            {
              key: 'humanOversight',
              label: 'Human oversight',
              width: '14%',
              render: (row) => dash(row.humanOversight),
            },
            {
              key: 'oversightExplanation',
              label: 'Oversight explanation',
              render: (row) => dash(row.oversightExplanation),
            },
          ]}
          rows={businessProcessRows}
        />


        <SectionTitle form={form}>6. Risk & impact</SectionTitle>
        <Table size="small" sx={{ tableLayout: 'fixed', color: 'inherit' }}>
          <TableBody>
            <TableRow>
              <FieldCell label="Uses artificial intelligence:" border={form.border} muted={form.muted}>
                {dash(usesAi)}
              </FieldCell>
              <FieldCell label="Known bias:" border={form.border} muted={form.muted}>
                {dash(report?.knownBias != null ? report.knownBias : risk.knownBias)}
              </FieldCell>
              <FieldCell label="Bias description:" border={form.border} muted={form.muted}>
                {dash(report?.biasDescription || risk.biasDescription)}
              </FieldCell>
            </TableRow>
          </TableBody>
        </Table>

        <SectionTitle form={form}>6b. Risk register</SectionTitle>
        <RiskRegisterReportList
          form={form}
          emptyMessage="No risks registered."
          rows={softwareRiskRows}
        />

        {usesAi && hasAiDetails ? (
          <>
            <SectionTitle form={form}>6a. Artificial intelligence — data & model</SectionTitle>
            <Table size="small" sx={{ tableLayout: 'fixed', color: 'inherit' }}>
              <TableBody>
                <TableRow>
                  <FieldCell label="Data sources used:" border={form.border} muted={form.muted} colSpan={3}>
                    {dash(dataSources)}
                  </FieldCell>
                </TableRow>
                <TableRow>
                  <FieldCell label="Personal data involved:" border={form.border} muted={form.muted}>
                    {dash(personalData)}
                  </FieldCell>
                  <FieldCell label="Sensitive data categories:" border={form.border} muted={form.muted}>
                    {dash(sensitive)}
                  </FieldCell>
                  <FieldCell label="Model type:" border={form.border} muted={form.muted}>
                    {dash(modelType)}
                  </FieldCell>
                </TableRow>
                <TableRow>
                  <FieldCell label="Training data origin:" border={form.border} muted={form.muted} colSpan={3}>
                    {dash(trainingOrigin)}
                  </FieldCell>
                </TableRow>
              </TableBody>
            </Table>
          </>
        ) : null}


        <SectionTitle form={form}>7. Compliance & documentation</SectionTitle>
        <Table size="small" sx={{ tableLayout: 'fixed', color: 'inherit' }}>
          <TableBody>
            <TableRow>
              <FieldCell label="Regulations:" border={form.border} muted={form.muted} colSpan={3}>
                {dash(regulations)}
              </FieldCell>
            </TableRow>
          </TableBody>
        </Table>
        <FormDataTable
          form={form}
          isDark={isDark}
          emptyMessage="No documents registered."
          columns={[
            {
              key: 'deploymentStatus',
              label: 'Deployment status',
              width: '28%',
              render: (row) => formatEnumLabel(row.deploymentStatus) || 'Unassigned',
            },
            { key: 'documents', label: 'Documents' },
          ]}
          rows={documentGroupRows}
        />


        <SectionTitle form={form}>8. Monitoring & review</SectionTitle>
        <FormDataTable
          form={form}
          isDark={isDark}
          emptyMessage="No monitoring reviews registered."
          columns={[
            { key: 'reviewAreaControl', label: 'Review Area / Control', width: '28%' },
            {
              key: 'riskRating',
              label: 'Risk Rating',
              width: '14%',
              render: (row) => formatEnumLabel(row.riskRating) || '—',
            },
            {
              key: 'status',
              label: 'Status',
              width: '14%',
              render: (row) => formatEnumLabel(row.status) || '—',
            },
            { key: 'nextReviewDate', label: 'Next review', width: '16%' },
            {
              key: 'approvalStatus',
              label: 'Approval status',
              render: renderApproval,
            },
          ]}
          rows={monitoringRows}
        />


        <SectionTitle form={form}>9. Incident & escalation</SectionTitle>
        <FormDataTable
          form={form}
          isDark={isDark}
          emptyMessage="No incidents registered."
          columns={[
            { key: 'incidentTicket', label: 'Incident Ticket', width: '16%' },
            { key: 'incidentDetails', label: 'Incident Description', width: '24%' },
            {
              key: 'severityLevel',
              label: 'Risk rating',
              width: '12%',
              render: (row) => formatEnumLabel(row.severityLevel) || '—',
            },
            {
              key: 'resolutionStatus',
              label: 'Status',
              width: '12%',
              render: (row) => formatEnumLabel(row.resolutionStatus) || '—',
            },
            { key: 'incidentResponseOwner', label: 'Incident responsible person', width: '18%' },
            {
              key: 'approvalStatus',
              label: 'Approval status',
              render: renderApproval,
            },
          ]}
          rows={incidentRows}
        />


        <SectionTitle form={form}>10. Change management</SectionTitle>
        <FormDataTable
          form={form}
          isDark={isDark}
          emptyMessage="No change records registered."
          columns={[
            { key: 'title', label: 'Title', width: '22%' },
            {
              key: 'changeType',
              label: 'Type',
              width: '12%',
              render: (row) => formatEnumLabel(row.changeType) || '—',
            },
            {
              key: 'priority',
              label: 'Priority',
              width: '12%',
              render: (row) => formatEnumLabel(row.priority) || '—',
            },
            {
              key: 'implementationStatus',
              label: 'Status',
              width: '14%',
              render: (row) => formatEnumLabel(row.implementationStatus) || '—',
            },
            { key: 'requestedBy', label: 'Requested by', width: '16%' },
            {
              key: 'approvalStatus',
              label: 'Approval status',
              render: renderApproval,
            },
          ]}
          rows={changeRows}
        />

        <SectionTitle form={form}>11. Integration</SectionTitle>
        <Box sx={{ px: 1.5, pb: 1 }}>
          <Typography variant="body2">
            Integrated with other systems: {isIntegrated ? 'Yes' : 'No'}
          </Typography>
        </Box>
        {isIntegrated ? (
          <FormDataTable
            form={form}
            isDark={isDark}
            emptyMessage="No integrations registered."
            columns={[
              { key: 'sourceSystem', label: 'Source system', width: '16%' },
              { key: 'targetSystem', label: 'Target system', width: '16%' },
              {
                key: 'integrationType',
                label: 'Type',
                width: '14%',
                render: (row) => formatEnumLabel(row.integrationType) || '—',
              },
              {
                key: 'dataFlowDirection',
                label: 'Direction',
                width: '12%',
                render: (row) => formatEnumLabel(row.dataFlowDirection) || '—',
              },
              { key: 'integrationMethod', label: 'Method / protocol', width: '14%' },
              {
                key: 'integrationStatus',
                label: 'Status',
                width: '12%',
                render: (row) => formatEnumLabel(row.integrationStatus) || '—',
              },
            ]}
            rows={integrationRows}
          />
        ) : null}

        <Box
          sx={{
            borderTop: form.border,
            py: 1.25,
            px: 1.5,
            textAlign: 'center',
            fontSize: '0.75rem',
            color: form.muted,
            '@media print': { color: '#333 !important', borderTop: '1px solid #1a1a1a !important' },
          }}
        >
          Please retain this report with the system register records for audit and compliance.
        </Box>
      </Paper>
    </Box>
  );
}
